import React, { useState, useEffect } from 'react';
import callApi from "../../../../utility/apiCaller";

const MainSection = () => {
    const [lenders, setLenders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchLenders = async () => {
            try {
                const [bucket1, bucket2] = await Promise.all([
                        callApi("v1/lender/active-bucket1-lenders", "post", {}, "loan"),
                        callApi("v1/lender/active-bucket2-lenders", "post", {}, "loan"),
                      ]);
                
                const combined =
                    bucket1?.status === "Success" && bucket2?.status === "Success"
                        ? [...bucket1.data.lenderList, ...bucket2.data.lenderList]
                        : [];

                // console.log("ALL LENDERS FROM API:", combined);        

                const validLenders = combined
                    .filter(lender => lender.gro_name && lender.gro_name.trim() !== "")
                    .map(lender => ({
                        _id: lender._id,
                        // Updated to strict production keys
                        brand_name: lender.brand_name || "",
                        company_name: lender.company_name || "",
                        gro_name: lender.gro_name || "",
                        gro_email: lender.gro_email || "",
                        gro_contact_no: lender.gro_contact_no || "",
                        website_link: lender.website_link || "",
                        terms_conditions_link: lender.terms_conditions_link || "",
                        privacy_policy_link: lender.privacy_policy_link || "",
                        address: lender.address || "",
                        timings: lender.timings || "",
                        kfs_doc: lender.kfs_doc || "",
                        rect_lender_logo: lender.rect_lender_logo || ""
                    }));

                setLenders(validLenders);
            } catch (error) {
                console.error("Failed to fetch digital lending partners:", error);
                setLenders([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchLenders();
    }, []);

    if (isLoading) {
        return <div className="text-gray-500 py-12 px-4 md:px-12 lg:px-24">Loading digital lending partners...</div>;
    }

    return (
        <section className="px-4 py-12 xs:py-5 md:px-12 lg:px-24 text-left w-full max-w-[1440px] mx-auto">

            {/* Header Area */}
            <div className="text-left mb-12">
                <h2 className="text-3xl xs:text-2xl md:text-6xl font-bomstad-semibold text-gray-900 mb-2">
                    Our Digital Lending Partners
                </h2>
                <p className="text-gray-500 text-md xs:text-sm max-w-5xl font-atkinson leading-relaxed">
                    We don't just generate leads; we help you acquire long-term customers
                    through data-driven strategies tailored to your business goals.
                </p>
            </div>

            {/* Grid layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 mt-5">
                {lenders.length > 0 ? (
                    lenders.map((lender, index) => (
                        <div
                            key={lender._id || index}
                            className="bg-white border border-gray-100 rounded-[24px] md:rounded-[32px] p-6 md:p-10 shadow-[0px_10px_40px_rgba(0,0,0,0.03)] hover:-translate-y-1 hover:shadow-[0px_15px_50px_rgba(0,0,0,0.06)] transition-all duration-300 flex flex-col"
                        >

                            {/* Logo Wrapper */}
                            <div className="flex justify-end items-center w-full h-[55px] md:h-[70px] mb-6 md:mb-8 mt-2">
                                {lender.rect_lender_logo && (
                                    <img
                                        src={lender.rect_lender_logo}
                                        alt={`${lender.brand_name} Logo`}
                                        className="h-full w-auto max-h-full max-w-[160px] md:max-w-[220px] object-contain object-right"
                                    />
                                )}
                            </div>

                            {/* Divider */}
                            <hr className="border-0 border-t border-gray-200 w-full mb-6 md:mb-8" />

                            {/* Details List */}
                            <div className="flex flex-col gap-4 md:gap-[20px]">

                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-[#9CA3AF] text-[13px] md:text-[14px] font-normal min-w-[100px]">Brand Name:</span>
                                    <span className="text-[#111827] text-[13px] md:text-[14px] font-semibold text-right break-words max-w-[65%]">{lender.brand_name}</span>
                                </div>

                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-[#9CA3AF] text-[13px] md:text-[14px] font-normal min-w-[100px]">Company Name:</span>
                                    <span className="text-[#111827] text-[13px] md:text-[14px] font-semibold text-right break-words max-w-[65%]">{lender.company_name}</span>
                                </div>

                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-[#9CA3AF] text-[13px] md:text-[14px] font-normal min-w-[100px]">GRO Name:</span>
                                    <span className="text-[#111827] text-[13px] md:text-[14px] font-semibold text-right break-words max-w-[65%]">{lender.gro_name}</span>
                                </div>

                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-[#9CA3AF] text-[13px] md:text-[14px] font-normal min-w-[100px]">GRO Email ID:</span>
                                    <span className="text-[#111827] text-[13px] md:text-[14px] font-semibold text-right break-words max-w-[65%]">{lender.gro_email}</span>
                                </div>

                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-[#9CA3AF] text-[13px] md:text-[14px] font-normal min-w-[100px]">GRO Contact No:</span>
                                    <span className="text-[#111827] text-[13px] md:text-[14px] font-semibold text-right break-words max-w-[65%]">{lender.gro_contact_no}</span>
                                </div>

                                {/* New Address Field */}
                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-[#9CA3AF] text-[13px] md:text-[14px] font-normal min-w-[100px]">Address:</span>
                                    <span className="text-[#111827] text-[13px] md:text-[14px] font-semibold text-right break-words max-w-[65%]">{lender.address || "N/A"}</span>
                                </div>

                                {/* New Timings Field */}
                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-[#9CA3AF] text-[13px] md:text-[14px] font-normal min-w-[100px]">Timings:</span>
                                    <span className="text-[#111827] text-[13px] md:text-[14px] font-semibold text-right break-words max-w-[65%]">{lender.timings || "N/A"}</span>
                                </div>

                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-[#9CA3AF] text-[13px] md:text-[14px] font-normal min-w-[100px]">Website Link:</span>
                                    {lender.website_link ? (
                                        <a href={lender.website_link} target="_blank" rel="noopener noreferrer" className="text-[#111827] text-[13px] md:text-[14px] font-semibold hover:text-blue-500 hover:underline truncate max-w-[65%] text-right">
                                            {lender.website_link.replace(/^https?:\/\//, '')}
                                        </a>
                                    ) : (
                                        <span className="text-[#111827] text-[13px] md:text-[14px] font-semibold text-right">N/A</span>
                                    )}
                                </div>

                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-[#9CA3AF] text-[13px] md:text-[14px] font-normal min-w-[100px]">KFS: (PDF)</span>
                                    {lender.kfs_doc ? (
                                        <a href={lender.kfs_doc} target="_blank" rel="noopener noreferrer" className="text-blue-500 text-[13px] md:text-[14px] font-semibold flex items-center gap-1.5 hover:underline text-right">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-[14px] h-[14px] md:w-[16px] md:h-[16px]">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                <polyline points="14 2 14 8 20 8"></polyline>
                                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                                <polyline points="10 9 9 9 8 9"></polyline>
                                            </svg>
                                            Document
                                        </a>
                                    ) : (
                                        <span className="text-[#111827] text-[13px] md:text-[14px] font-semibold text-right">N/A</span>
                                    )}
                                </div>

                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-[#9CA3AF] text-[13px] md:text-[14px] font-normal min-w-[100px]">T&C:</span>
                                    {lender.terms_conditions_link ? (
                                        <a href={lender.terms_conditions_link} target="_blank" rel="noopener noreferrer" className="text-[#111827] text-[13px] md:text-[14px] font-semibold hover:text-blue-500 hover:underline truncate max-w-[65%] text-right">
                                            {lender.terms_conditions_link}
                                        </a>
                                    ) : (
                                        <span className="text-[#111827] text-[13px] md:text-[14px] font-semibold text-right">N/A</span>
                                    )}
                                </div>

                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-[#9CA3AF] text-[13px] md:text-[14px] font-normal min-w-[100px]">Privacy Policy:</span>
                                    {lender.privacy_policy_link ? (
                                        <a href={lender.privacy_policy_link} target="_blank" rel="noopener noreferrer" className="text-[#111827] text-[13px] md:text-[14px] font-semibold hover:text-blue-500 hover:underline truncate max-w-[65%] text-right">
                                            {lender.privacy_policy_link}
                                        </a>
                                    ) : (
                                        <span className="text-[#111827] text-[13px] md:text-[14px] font-semibold text-right">N/A</span>
                                    )}
                                </div>

                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-gray-500 text-sm md:text-base mt-8">No partners found matching the criteria.</div>
                )}
            </div>
        </section>
    );
};

export default MainSection;